import { POLICY_DOCUMENTS } from '@telestore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import {
  Button,
  Empty,
  ErrorBox,
  Panel,
  Pill,
  Row,
  Rows,
  Spinner,
} from '../components/ui.js';

/**
 * Trust & safety, merchant side (CLAUDE.md §6.7).
 *
 * Five things on one page, in the order a merchant meets them: whether they
 * are suspended, what they still have to accept, what they may not sell, a
 * returns policy they can adopt in one tap, and the tax heads-up.
 *
 * The suspension banner is first on purpose. A merchant whose shop has stopped
 * selling has exactly one question, and every other panel here is noise until
 * it is answered.
 */
export function PolicyPage() {
  const { shopId, locale, t } = useSession();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const policy = useQuery({
    queryKey: ['policy', shopId],
    queryFn: () => api.policy(shopId!),
    enabled: Boolean(shopId),
  });

  const reports = useQuery({
    queryKey: ['shop-reports', shopId],
    queryFn: () => api.shopReports(shopId!),
    enabled: Boolean(shopId),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: (documents: string[]) => api.acceptPolicy(shopId!, documents),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['policy', shopId] });
      setError(null);
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  const adopt = useMutation({
    mutationFn: (templateId: string) => api.adoptReturnTemplate(shopId!, templateId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['policy', shopId] });
      void queryClient.invalidateQueries({ queryKey: ['shop', shopId] });
      setError(null);
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  if (policy.isPending) return <Spinner label={t('common.loading')} />;
  if (policy.isError) {
    return (
      <ErrorBox
        message={policy.error instanceof ApiError ? policy.error.message : t('errors.internal')}
      />
    );
  }

  const { status, prohibited, returnPolicyTemplates, taxNotice, shop } = policy.data;
  const text = (value: { km: string; en: string }) => value[locale] || value.en;

  return (
    <div className="flex flex-col gap-4">
      {shop.status !== 'active' ? (
        <div
          className="panel p-4"
          style={{ borderColor: 'var(--color-danger)', background: 'var(--color-danger-soft)' }}
          role="alert"
        >
          <p className="font-semibold" style={{ color: 'var(--color-danger)' }}>
            {t('trust.suspendedBanner')}
          </p>
          {shop.suspendedReason ? <p className="mt-1 text-sm">{shop.suspendedReason}</p> : null}
          <p className="mt-2 text-sm" style={{ color: 'var(--color-ink-soft)' }}>
            {t('trust.suspendedBannerHint')}
          </p>
        </div>
      ) : null}

      {status.outstanding.length > 0 ? (
        <Panel title={t('trust.outstanding')}>
          <p className="text-sm" style={{ color: 'var(--color-ink-soft)' }}>
            {t('trust.outstandingHint')}
          </p>
          <ul className="mt-3 flex flex-col gap-1 text-sm">
            {status.outstanding.map((document) => (
              <li key={document}>• {documentLabel(document, t)}</li>
            ))}
          </ul>
          <Button className="mt-4" disabled={accept.isPending} onClick={() => accept.mutate([...status.outstanding])} >
            {accept.isPending ? t('common.loading') : t('trust.acceptAll')}
          </Button>
        </Panel>
      ) : null}

      <Panel title={t('trust.policy')} padded={false}>
        <Rows>
          {POLICY_DOCUMENTS.map((document) => {
            const entry = status.accepted.find((item) => item.document === document);
            const current = !entry || entry.outdated ? null : entry;
            return (
              <Row key={document}>
                <span className="flex-1">{documentLabel(document, t)}</span>
                {current ? (
                  <Pill tone="positive">
                    {t('trust.accepted', { date: current.acceptedAt.slice(0, 10) })}
                  </Pill>
                ) : (
                  <Pill tone="warn">{entry ? t('trust.updated') : t('common.required')}</Pill>
                )}
              </Row>
            );
          })}
        </Rows>
      </Panel>

      <Panel title={t('trust.prohibitedGoods')}>
        <p className="text-sm" style={{ color: 'var(--color-ink-soft)' }}>
          {t('trust.prohibitedIntro')}
        </p>
        <div className="mt-3 flex flex-col gap-3">
          {prohibited.map((category) => (
            <details key={category.id}>
              <summary className="cursor-pointer text-sm font-medium">
                {text(category.title)}
              </summary>
              <p className="mt-1 text-sm" style={{ color: 'var(--color-ink-soft)' }}>
                {text(category.detail)}
              </p>
              <p className="mt-1 text-xs" style={{ color: 'var(--color-ink-faint)' }}>
                {text(category.examples)}
              </p>
            </details>
          ))}
        </div>
      </Panel>

      <Panel title={t('trust.returnTemplate')}>
        <p className="text-sm" style={{ color: 'var(--color-ink-soft)' }}>
          {t('trust.returnTemplateHint')}
        </p>
        <div className="mt-3 flex flex-col gap-3">
          {returnPolicyTemplates.map((template) => (
            <div
              key={template.id}
              className="rounded-[10px] p-3"
              style={{ border: '1px solid var(--color-line)' }}
            >
              <p className="font-medium">{text(template.label)}</p>
              <p className="text-xs" style={{ color: 'var(--color-ink-faint)' }}>
                {text(template.suitedTo)}
              </p>
              <p
                className="mt-2 whitespace-pre-line text-xs"
                style={{ color: 'var(--color-ink-soft)' }}
              >
                {text(template.body)}
              </p>
              <Button variant="outline" className="mt-3" disabled={adopt.isPending} onClick={() => adopt.mutate(template.id)} >
                {t('trust.adoptTemplate')}
              </Button>
            </div>
          ))}
        </div>
      </Panel>

      {/*
        §6.7 wants this visible enough that a merchant cannot say they were
        never told, and worded so it is plainly a heads-up rather than advice.
      */}
      <Panel title={text(taxNotice.title)}>
        <p className="text-sm" style={{ color: 'var(--color-ink-soft)' }}>
          {text(taxNotice.body)}
        </p>
        <a
          className="mt-3 inline-block text-sm font-medium underline"
          href={taxNotice.url}
          target="_blank"
          rel="noreferrer noopener"
        >
          {text(taxNotice.linkLabel)}
        </a>
      </Panel>

      {reports.isSuccess ? (
        <Panel title={t('trust.reportsAboutYou')} padded={false}>
          {reports.data.reports.length === 0 ? (
            <Empty title={t('trust.noReports')} />
          ) : (
            <Rows>
              {reports.data.reports.map((report) => (
                <Row key={report.id}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{report.reason}</p>
                    {report.detail ? (
                      <p className="truncate text-xs" style={{ color: 'var(--color-ink-soft)' }}>
                        {report.detail}
                      </p>
                    ) : null}
                  </div>
                  <Pill
                    tone={
                      report.status === 'upheld'
                        ? 'danger'
                        : report.status === 'dismissed'
                          ? 'neutral'
                          : 'warn'
                    }
                  >
                    {report.status}
                  </Pill>
                </Row>
              ))}
            </Rows>
          )}
        </Panel>
      ) : null}

      {error ? <ErrorBox message={error} /> : null}
    </div>
  );
}

function documentLabel(
  document: string,
  t: ReturnType<typeof useSession>['t'],
): string {
  if (document === 'tos') return t('trust.terms');
  if (document === 'privacy') return t('trust.privacy');
  return t('trust.prohibitedGoods');
}
